import { Component, NgModule, ViewEncapsulation, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { BrowserModule, DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { debounceTime, takeWhile, takeUntil, switchMap, delay, timeout, retryWhen, tap } from 'rxjs/operators';
import { Subject, interval } from 'rxjs';
import { environment } from 'src/environments/environment';
import { formatPhoneNumber, validatePhoneNumber } from '../../../_helpers/phone.utils';
import { getSubdomainPreview, validateSubdomainFormat } from '../../../_helpers/utils.index';
import { FormDataService } from 'src/app/_services/form-data.service';

// PrimeNG Imports for Angular 13
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { ImageModule } from 'primeng/image';
import { DropdownModule } from 'primeng/dropdown';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ProgressBarModule } from 'primeng/progressbar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { UserService } from 'src/app/_services/user.service';
import { Router } from '@angular/router';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { LocationsService } from 'src/app/_services/locations.service';
import { ConfirmationService } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { Location } from '@angular/common';
import { GlobalService } from 'src/app/_services/global.service';
import { ProductsService } from 'src/app/_services/products.service';
import { SignupService } from 'src/app/_services/signup.service';
import { CategoryService } from 'src/app/_services/category.service';

@Component({
  selector: 'app-store-setup',
  templateUrl: './store-setup.component.html',
  styleUrls: ['./store-setup.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [ConfirmationService, MessageService]
})
export class StoreSetupComponent implements OnInit, OnDestroy {
  _global = GlobalService
  private readonly activeStepStoragePrefix = 'welcome-store-setup-active-step';
  totalSteps = 3;  // Only 3 steps are currently enabled: Business Details, Choose Layout, Add Products
  activeIndex: number | null = 0;
  completedSteps = new Set<number>();
  isSuccess = false;
  step1CompletedOnPrefetch = false; // Track if Step 1 was pre-completed

  TemplateData: any = [
    { templateID: 3, templateName: 'Timelore', previewUrl: 'https://timelore.lincsell.com/', imageUrl: 'https://cloudstoragels.blob.core.windows.net/commonfiles/template-3.webp' },
    { templateID: 2, templateName: 'Luminest', previewUrl: 'https://luminestdemo.lincsell.com/', imageUrl: 'https://cloudstoragels.blob.core.windows.net/commonfiles/template-2.webp' },
    { templateID: 1, templateName: 'Trendovo', previewUrl: 'https://trendovodemo.lincsell.com/', imageUrl: 'https://cloudstoragels.blob.core.windows.net/commonfiles/template-1.webp' },

  ]

  // RxJS Cleanup Subject
  private destroy$ = new Subject<void>();

  // Reactive Form Groups
  businessDetailsForm!: FormGroup;
  designStoreForm!: FormGroup;
  addProductsForm!: FormGroup;
  shippingSettingsForm!: FormGroup;

  // Step Visibility Flags
  showBusinessDetails: boolean = true;
  showDesignStore: boolean = true;
  showAddProducts: boolean = true;
  showPaymentProvider: boolean = false;   // Hidden - not part of the 3 active steps
  showShippingSettings: boolean = false;  // Hidden - not part of the 3 active steps
  showPreviewStore: boolean = false;      // Hidden - not part of the 3 active steps

  // UI State
  imgPreview: SafeUrl | string | null = null;
  logoPreview: string | null = null;
  private productImageFile: File | null = null;
  private productImagePreviewUrl: string | null = null;
  selectedTheme: any = null;
  pendingThemeSelection: any = null;
  showLayoutConfirmDialog: boolean = false;
  productCount: number = 0;
  products: any[] = [];
  isAddingAnotherProduct: boolean = false;

  // Subdomain availability tracking
  subdomainAvailable: boolean | null = null;
  subdomainCheckError: string = '';
  subdomainCheckLoading: boolean = false;
  private lastCheckedSubdomain: string = '';

  // Redirect timer on success screen
  redirectCountdown: number = 0;
  isCountingDown: boolean = false;
  private successTimerStarted: boolean = false;
  private countdownSubscription: any;

  // Track which fields are pre-filled (for hiding them)
  prefilledFields = {
    businessName: false,
    businessType: false,
    businessDescription: false,
    streetAddress: false,
    apartment: false,
    city: false,
    state: false,
    zipCode: false,
    country: true,
    businessPhone: false
  };

  // Data Arrays
  businessTypes: any[] = [];
  countries = [
    { name: 'US', code: 'US' }
  ];
  usStates: any[] = [];
  rateTypes: any[] = [];
  themeList: any;
  isConnectStripeAccountLoading: boolean;
  fetchingStripeStatus: boolean;
  isStripeConnected: any;
  stripAccountStatus: any;
  isConnectStripeAccountSuccess: boolean;
  isConnectStripeAccountErrorText: string;
  isConnectStripeAccountError: boolean;
  private isUserInfoRefreshInProgress = false;

  // Loading state for parallel step checks
  isLoadingStepStatus: boolean = false;

  // Flag to track if initial setup checks are complete
  isInitializationComplete: boolean = false;

  get progressPercent(): number {
    return (this.completedSteps.size / this.totalSteps) * 100;
  }
  get WebsiteLink() {
    let websiteDomain: string = '';
    websiteDomain = window.location.origin + '/'; // this._globals.whiteLabel.domain
    var n = websiteDomain.lastIndexOf('/');
    var result = websiteDomain.substring(n + 1);

    if (result == '') {
      websiteDomain = websiteDomain.substring(
        0,
        websiteDomain.length - 1
      );
    }
    return websiteDomain;
  }

  checkStripeAccountStatus(): Promise<void> {
    return new Promise((resolve) => {
      // This should never reject - it should always resolve to allow other steps to continue
      this.fetchingStripeStatus = true;
      this._businessSetupService.CheckConnectAccount().subscribe({
        next: (response) => {
          if (response.success) {
            this.isStripeConnected = response.data.chargesEnabled || false;
            this.stripAccountStatus = response.data.status;
            if (this.isStripeConnected) {
              this.completeStep(4);
            }
            console.log('✓ Stripe account status checked successfully:', {
              isConnected: this.isStripeConnected,
              status: this.stripAccountStatus
            });
          } else {
            console.warn('⚠ Stripe check returned unsuccessful response:', response);
            this.isStripeConnected = false;
          }
          this.fetchingStripeStatus = false;
          resolve(); // Always resolve
        },
        error: (error) => {
          console.warn('⚠ Error checking Stripe account status:', error);
          this.isStripeConnected = false;
          this.fetchingStripeStatus = false;
          resolve(); // Always resolve - don't block initialization
        },
        complete: () => {
          this.fetchingStripeStatus = false;
        }
      });
    });
  }
  openConnectStripeAccount() {
    this.isConnectStripeAccountLoading = true;
    const UserURL = this._global.webStoreConfiguration?.webStoreHost;
    const currentSetup = this.getCurrentBusinessSetup();
    const accountConfig = {
      "Email": this._global.userInfo.username,
      "ReturnUrl": this.WebsiteLink + "/success",
      "RefreshUrl": this.WebsiteLink + "/reauth",
      "UserId": currentSetup?.userID ?? currentSetup?.userId ?? this._global.userInfo?.userID,
    }
    this._businessSetupService.SetupConnectAccount(accountConfig).subscribe({
      next: (response) => {

        if (response.data.onboardingUrl != undefined && response.data.onboardingUrl != null) {
          window.open(response.data.onboardingUrl, '_blank');
          this.isConnectStripeAccountSuccess = true;
          this.isConnectStripeAccountError = false;
          this.isConnectStripeAccountErrorText = '';
        } else {
          this.isConnectStripeAccountError = true;
          this.isConnectStripeAccountErrorText = response.message || 'Failed to initiate Stripe account connection.';
        }
      },
      error: (error) => {
        this.isConnectStripeAccountError = true;
        this.isConnectStripeAccountLoading = false;
        this.isConnectStripeAccountErrorText = 'An error occurred while connecting to Stripe. Please try again later.';
      },
      complete: () => {
        this.isConnectStripeAccountLoading = false;
      }
    });
  }
  isStepCompleted(step: number): boolean {
    return this.completedSteps.has(step);
  }

  loadThemeList(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._businessSetup.getThemeConfigSettings().subscribe({
        next: (res) => {
          if (res.success) {
            this.themeList = res.data;

            if (res.data && (Array.isArray(res.data) ? res.data.length > 0 : true)) {

              if (res.data.webStoreHost)
                this.completedSteps.add(2);
              else {
                res.data.forEach(element => {
                  if (element.templateID) {
                    const template = this.TemplateData.find(t => t.templateID === element.templateID);
                    if (template) {
                      element.name = template.templateName;
                      element.demoUrl = template.previewUrl;
                      element.imageUrl = template.imageUrl;
                    }
                  }
                });
              }
            }
            else {
              this.completedSteps.add(2);
            }
            resolve();
          } else {
            console.error('✗ Theme loading failed - response success was false');
            reject(new Error('Failed to load themes'));
          }
        },
        error: (error) => {
          console.error('✗ Error loading themes:', error);
          reject(error);
        }
      });
    });
  }

  private loadWebConfiguration(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this._userService.getWebStoreConfiguration().subscribe({
          next: (res) => {
            if (res.success) {
              GlobalService.webStoreConfiguration = res.data;
              console.log( GlobalService.webStoreConfiguration)
              if (res.data && (Array.isArray(res.data) ? res.data.length > 0 : true)) {

              }
              else {
                this.completedSteps.add(2);
              }
              resolve();
            } else {
              resolve(); // Still resolve, not an error
            }
          },
          error: (error) => {
            resolve(); // Don't reject, this is optional
          }
        });
      } catch (error) {
        resolve(); // Don't reject, this is optional
      }
    });
  }

  private getInventory(): Promise<void> {
    return new Promise((resolve) => {
      try {
        this._productServices.getStoreSetupInventoryV3(0, 10).subscribe((res: any) => {
          if (res.success) {
            const items = Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res?.data?.items)
                ? res.data.items
                : Array.isArray(res?.items)
                  ? res.items
                  : [];

            const totalRecordsRaw = res?.totalRecords ?? res?.data?.totalRecords;
            const totalRecords = Number(totalRecordsRaw);
            const safeCount = Number.isFinite(totalRecords) && totalRecords > 0 ? totalRecords : items.length;

            if (safeCount > 0) {
              this.completedSteps.add(3);
              this.productCount = safeCount;
              this.products = items;
            }
            resolve();
          } else {
            resolve();
          }
        }, (error) => {
          resolve(); // Don't reject, this is optional
        });
      } catch (error) {
        resolve(); // Don't reject, this is optional
      }
    });
  }
  ngOnInit(): void {
    this.initializeComponent();

    // Subscribe to business details form changes for subdomain auto-population
    this.businessDetailsForm.get('businessName')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.updateSubdomain(value);
      });

    // Subscribe to subdomain validation with debouncing
    this.designStoreForm.get('subdomain')?.valueChanges
      .pipe(
        debounceTime(800),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        this.validateSubdomain(value);
      });
  }

  ngOnDestroy(): void {
    this.revokeProductImagePreviewUrl();
    // Emit complete signal to unsubscribe all observables
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initializeComponent(): Promise<void> {
    try {
      this.isLoadingStepStatus = true;
      this.isInitializationComplete = false;

      // First, ensure user info is loaded (blocking prerequisite)
      await this.ensureUserInfoLoaded();

      // Load theme list
      await this.loadThemeList();

      // Run step completion checks in parallel

      await Promise.all([
        //this.checkStripeAccountStatus(),
        //this.loadWebConfiguration(),
        this.getInventory()
      ]);
      this.isLoadingStepStatus = false;

      // Pre-fill first step with user information (without opening accordion yet)
      this.prefillBusinessDetailsForm();

      // Mark initialization as complete FIRST
      this.isInitializationComplete = true;

      // THEN set which step to open
      this.setActiveIndexToFirstIncompleteStep();
    } catch (error) {
      this.isLoadingStepStatus = false;
      this.isInitializationComplete = true;
      console.error('Failed to initialize component:', error);
      this._messageService.add({
        severity: 'error',
        summary: 'Initialization Error',
        detail: 'Failed to load required data. Please refresh the page.',
        sticky: false
      });
    }
  }

  // goToEditer() {
  //   try {
  //     const config = this._global.webStoreConfiguration
  //     const selectedConfig = Array.isArray(config)
  //       ? (config.find(v => v?.isDefault) ?? config[0])
  //       : config
  //     const url = (selectedConfig?.demoUrl || selectedConfig?.webStoreHost || '').trim()

  //     if (!url) {
  //       console.warn('No store URL available to open.')
  //       return
  //     }

  //     const newWindow = window.open(url, '_blank', 'noopener')
  //     if (!newWindow) {
  //       window.location.assign(url)
  //     }
  //   }
  //   catch (error) {
  //     console.error('Error opening store editor:', error);
  //   }
  // }

  goToEditer() {
    try {
      if (Array.isArray(this._global.webStoreConfiguration)) {
        let iswebStoreSelectedDefault: boolean = false
        iswebStoreSelectedDefault = this._global.webStoreConfiguration.filter(v => v.isDefault === true)?.length > 0 ? true : false

      } else if (typeof this._global.webStoreConfiguration === 'object' && this._global.webStoreConfiguration !== null) {

        window.open(this._global.webStoreConfiguration.demoUrl, '_blank')
      }
    }
    catch (error) {
      console.error('Error opening store editor:', error);
    }
  }


  private prefillBusinessDetailsForm(): void {
    try {
      const userInfo = this._global.userInfo;

      if (!userInfo) {
        console.error('ERROR: userInfo is null/undefined');
        return;
      }

      if (!userInfo.businessSetup?.length) {
        return;
      }

      const businessSetup = userInfo.businessSetup[0];

      // Prepare data object for form patching
      const prefillData: any = {};

      // Fill Business Details

      if (businessSetup.businessName) {

        if (businessSetup.businessName.toLowerCase() === businessSetup.businessDisplayName?.toLowerCase()) {
          prefillData['businessName'] = "";

          this.prefilledFields.businessName = false;
        }
        else {
          prefillData['businessName'] = businessSetup.businessDisplayName;

          this.prefilledFields.businessName = true;
        }

      }

      if (businessSetup.industry) {

        // Parse the combined industry field: "Industry Name || description"
        let industryName = businessSetup.industry;
        let description = '';

        if (businessSetup.industry.includes(' || ')) {
          const parts = businessSetup.industry.split(' || ');
          industryName = parts[0].trim();
          description = parts[1].trim();
        }

        // Try to find by name
        let matchingType = this.businessTypes.find(bt => bt.name === industryName);

        if (matchingType) {
          prefillData['businessType'] = matchingType.id;
          this.prefilledFields.businessType = true;
        }

        // Also set the description if found
        if (description && !businessSetup.description) {
          prefillData['businessDescription'] = description;
          this.prefilledFields.businessDescription = true;
        } else if (businessSetup.description) {
          prefillData['businessDescription'] = businessSetup.description;
          this.prefilledFields.businessDescription = true;
        } else {
          // If business description is empty, also show business name field for user to fill
          this.prefilledFields.businessName = false;
        }
      } else {
        // If industry is empty, show business name field
        this.prefilledFields.businessName = false;
      }
      if (!businessSetup.country)
        businessSetup.country = 'US';
      // Fill Address Information - Check if ALL mandatory address fields exist
      const hasAllAddressFields = businessSetup.address && businessSetup.city && businessSetup.state && businessSetup.zip && businessSetup.country;

      if (businessSetup.address) {
        // Parse address using || separator to separate street address and apartment
        // Format: "123 Main St || Suite 100"
        let streetAddress = businessSetup.address;
        let apartment = '';

        if (businessSetup.address.includes(' || ')) {
          const parts = businessSetup.address.split(' || ');
          streetAddress = parts[0].trim();
          apartment = parts[1].trim();
        }

        prefillData['streetAddress'] = streetAddress;
        // Only mark as prefilled if ALL mandatory address fields are complete
        if (hasAllAddressFields) {
          this.prefilledFields.streetAddress = true;
          this.prefilledFields.apartment = true; // Hide apartment if address is pre-filled
        }
        if (apartment) {
          prefillData['apartment'] = apartment;
        }
      }

      if (businessSetup.city) {
        prefillData['city'] = businessSetup.city;
        // Only mark as prefilled if ALL mandatory address fields are complete
        if (hasAllAddressFields) {
          this.prefilledFields.city = true;
          this.prefilledFields.apartment = true; // Hide apartment if city is pre-filled
        }
      }

      if (businessSetup.state) {
        prefillData['state'] = businessSetup.state;
        // Only mark as prefilled if ALL mandatory address fields are complete
        if (hasAllAddressFields) {
          this.prefilledFields.state = true;
          this.prefilledFields.apartment = true; // Hide apartment if state is pre-filled
        }
      }

      if (businessSetup.zip && businessSetup.zip !== '00000') {
        prefillData['zipCode'] = businessSetup.zip;
        // Only mark as prefilled if ALL mandatory address fields are complete
        if (hasAllAddressFields) {
          this.prefilledFields.zipCode = true;
          this.prefilledFields.apartment = true; // Hide apartment if zip is pre-filled
        }
      }

      if (businessSetup.country) {
        prefillData['country'] = businessSetup.country;
        // Only mark as prefilled if ALL mandatory address fields are complete
        if (hasAllAddressFields) {
          this.prefilledFields.country = true;
          this.prefilledFields.apartment = true; // Hide apartment if country is pre-filled
        }
      }

      if (businessSetup.phone && businessSetup.phone !== '0000000000') {
        prefillData['businessPhone'] = formatPhoneNumber(businessSetup.phone);
        this.prefilledFields.businessPhone = true;
      }

      // Patch the form with collected data
      if (Object.keys(prefillData).length > 0) {
        this.businessDetailsForm.patchValue(prefillData, { emitEvent: false });

        // Auto-populate subdomain from business name if not already set
        if (this.businessName && !this.businessSubdomain) {
          this.updateSubdomain(this.businessName);
        }

        // this._messageService.add({ 
        //   severity: 'success', 
        //   summary: 'Data Loaded', 
        //   detail: 'Your business information has been pre-filled.',
        //   life: 3000 
        // });

        // Check if ALL required Step 1 fields are pre-filled
        const allFieldsPreFilled =
          this.prefilledFields.businessName &&
          this.prefilledFields.businessType &&
          this.prefilledFields.businessDescription &&
          this.prefilledFields.streetAddress &&
          this.prefilledFields.city &&
          this.prefilledFields.state &&
          this.prefilledFields.zipCode &&
          this.prefilledFields.businessPhone;

        if (allFieldsPreFilled) {
          // Mark Step 1 as automatically completed
          this.completedSteps.add(1);
          this.step1CompletedOnPrefetch = true; // Mark that it was pre-completed
          // Don't set activeIndex here - let setActiveIndexToFirstIncompleteStep handle it
        }
      }

      // If Step 2 is already completed, load the selected theme
      if (this.isStepCompleted(2)) {
        this.loadSelectedTheme();
      }

      // Note: setActiveIndexToFirstIncompleteStep() will be called from initializeComponent()
      // after isInitializationComplete flag is set
    } catch (error) {
      console.error('=== ERROR pre-filling business details form:', error);
      console.error('Error stack:', (error as any)?.stack);
    }
  }

  private setActiveIndexToFirstIncompleteStep(): void {
    // Keep accordion closed until initialization is complete and spinner is hidden
    if (!this.isInitializationComplete || this.isLoadingStepStatus) {
      this.activeIndex = null;
      return;
    }

    const persistedActiveIndex = this.readPersistedActiveStep();
    if (persistedActiveIndex !== null && this.isValidRestorableStepIndex(persistedActiveIndex)) {
      this.activeIndex = persistedActiveIndex;
      return;
    }

    // Find the first incomplete step (1, 2, or 3)
    for (let step = 1; step <= this.totalSteps; step++) {
      if (!this.isStepCompleted(step)) {
        // Set activeIndex to the first incomplete step (0-based)
        this.activeIndex = step - 1;
        this.persistActiveStep(this.activeIndex);
        return;
      }
    }

    // If all steps are completed, show success view
    this.isSuccess = true;
    this.persistActiveStep(null);

    this.startSuccessCountdown(); // START TIMER AUTO


  }

  private getActiveStepStorageKey(): string {
    const setup = this._global.userInfo?.businessSetup?.[0];
    const businessId = String(setup?.businessId ?? setup?.id ?? 'default-business');
    const userId = String(this._global.userInfo?.userID ?? this._global.userInfo?.userId ?? 'default-user');
    return `${this.activeStepStoragePrefix}-${userId}-${businessId}`;
  }

  private persistActiveStep(index: number | null): void {
    try {
      const key = this.getActiveStepStorageKey();
      if (index === null || index === undefined) {
        sessionStorage.removeItem(key);
        return;
      }
      sessionStorage.setItem(key, String(index));
    } catch {
      // Ignore sessionStorage errors.
    }
  }

  private readPersistedActiveStep(): number | null {
    try {
      const raw = sessionStorage.getItem(this.getActiveStepStorageKey());
      if (raw === null || raw === undefined || raw === '') {
        return null;
      }

      const parsed = Number(raw);
      if (!Number.isInteger(parsed)) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  private isValidRestorableStepIndex(index: number): boolean {
    if (index < 0 || index >= this.totalSteps) {
      return false;
    }

    const stepNumber = index + 1;
    for (let step = 1; step < stepNumber; step++) {
      if (!this.isStepCompleted(step)) {
        return false;
      }
    }

    return true;
  }

  private loadSelectedTheme(): void {
    // Get the first theme from the list as default selected
    if (this.themeList && this.themeList.length > 0) {
      const defaultTheme = this.themeList.find((t: any) => t.isDefault === true) || this.themeList[0];
      this.selectedTheme = defaultTheme;
    }
  }

  private ensureUserInfoLoaded(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.hasLoadedUserInfo() || this.hydrateUserInfoFromSession()) {
        resolve();
        return;
      }

      this.requestUserInfoRefresh();

      const maxAttempts = 60;
      const pollInterval = 500; // ms
      let isSettled = false;
      let pollSubscription: any;

      const settleResolve = () => {
        if (isSettled) return;
        isSettled = true;
        resolve();
      };

      const settleReject = (error: any) => {
        if (isSettled) return;
        isSettled = true;
        reject(error);
      };

      pollSubscription = interval(pollInterval)
        .pipe(
          // Emit the attempt number
          // Stop after maxAttempts
          takeWhile((attempt) => {
            return attempt < maxAttempts;
          }, true), // inclusive = true to run condition one last time

          // Stop polling when userInfo is loaded OR destroy$ is triggered
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: () => {
            if (this.hasLoadedUserInfo() || this.hydrateUserInfoFromSession()) {
              pollSubscription?.unsubscribe();
              settleResolve();
              return;
            }

            if (!this.isUserInfoRefreshInProgress) {
              this.requestUserInfoRefresh();
            }
          },
          error: (error) => {
            console.error('Error during userInfo polling:', error);
            settleReject(error);
          },
          complete: () => {
            // Check one final time if userInfo loaded
            if (this.hasLoadedUserInfo() || this.hydrateUserInfoFromSession()) {
              settleResolve();
            } else {
              console.error(`✗ User information failed to load after ${maxAttempts} attempts`);
              console.error('Final state - userInfo:', this._global.userInfo);
              settleReject(new Error('User information failed to load'));
            }
          }
        });
    });
  }

  private hasLoadedUserInfo(): boolean {
    return !!(this._global.userInfo && this._global.userInfo.businessSetup?.length > 0);
  }

  private safeParseJson(raw: string | null): any {
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private hydrateUserInfoFromSession(): boolean {
    if (this.hasLoadedUserInfo()) {
      return true;
    }

    const businessInfo = this.safeParseJson(sessionStorage.getItem('business-Info'));
    if (!businessInfo) {
      return false;
    }

    const localUser = this.safeParseJson(localStorage.getItem('user'));
    const loginUser = this.safeParseJson(sessionStorage.getItem('login-user'));
    const existing = this._global.userInfo || {};
    const userName =
      existing?.username
      || existing?.userName
      || localUser?.userName
      || loginUser?.Username
      || loginUser?.username
      || '';
    const userId =
      existing?.userID
      || existing?.userId
      || businessInfo?.userID
      || businessInfo?.userId
      || '';

    this._global.userInfo = {
      ...existing,
      userID: userId,
      userId: userId,
      username: userName,
      userName: userName,
      businessSetup: [businessInfo]
    };

    return this.hasLoadedUserInfo();
  }

  private requestUserInfoRefresh(): void {
    if (this.isUserInfoRefreshInProgress || this.hasLoadedUserInfo()) {
      return;
    }

    this._userService.setCurrentUserFromLocalStorage();
    const localUser = this.safeParseJson(localStorage.getItem('user'));
    const loginUser = this.safeParseJson(sessionStorage.getItem('login-user'));
    const userName = String(
      this._global.userInfo?.username
      || this._global.userInfo?.userName
      || this._userService.currentUserObj?.userName
      || localUser?.userName
      || loginUser?.Username
      || loginUser?.username
      || ''
    ).trim();

    if (!userName) {
      return;
    }

    this.isUserInfoRefreshInProgress = true;
    this._userService.getUserByUserName(userName).subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this._global.userInfo = res.data;
          const setup = res.data?.businessSetup?.[0];
          if (setup) {
            sessionStorage.setItem('business-Info', JSON.stringify(setup));
          }
        }
      },
      error: () => {
        // Keep polling/session fallback active.
      },
      complete: () => {
        this.isUserInfoRefreshInProgress = false;
      }
    });
  }

  private getCurrentBusinessSetup(): any {
    return this._global.userInfo?.businessSetup?.[0] || this.safeParseJson(sessionStorage.getItem('business-Info')) || null;
  }

  private revokeProductImagePreviewUrl(): void {
    if (this.productImagePreviewUrl) {
      URL.revokeObjectURL(this.productImagePreviewUrl);
      this.productImagePreviewUrl = null;
    }
  }

  private clearProductImageSelection(): void {
    this.revokeProductImagePreviewUrl();
    this.productImageFile = null;
    this.imgPreview = null;
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Unable to read image file.'));
      reader.readAsDataURL(file);
    });
  }

  private async resolveProductImageData(): Promise<string | null> {
    if (this.productImageFile) {
      return await this.readFileAsDataUrl(this.productImageFile);
    }

    return typeof this.imgPreview === 'string' && this.imgPreview.startsWith('data:')
      ? this.imgPreview
      : null;
  }

  handleImageUpload(event: any) {
    const input = event?.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    if (!String(file.type || '').startsWith('image/')) {
      this._messageService.add({ severity: 'error', summary: 'Invalid File', detail: 'Please upload an image file only.', sticky: false });
      if (input) input.value = '';
      return;
    }

    const maxFileSize = 5 * 1024 * 1024;
    if (file.size > maxFileSize) {
      this._messageService.add({ severity: 'error', summary: 'File Too Large', detail: 'Product image must be 5MB or smaller.', sticky: false });
      if (input) input.value = '';
      return;
    }

    this.revokeProductImagePreviewUrl();
    this.productImageFile = file;
    this.productImagePreviewUrl = URL.createObjectURL(file);
    this.imgPreview = this._domSanitizer.bypassSecurityTrustUrl(this.productImagePreviewUrl);
  }

  removeImage() {
    this.clearProductImageSelection();
  }

  handleLogoUpload(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.logoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  removeLogo() {
    this.logoPreview = null;
  }

  private normalizeMatcherValue(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private getHostFromUrl(url: string): string {
    const normalizedUrl = this.normalizeMatcherValue(url);
    if (!normalizedUrl) {
      return '';
    }

    try {
      return new URL(normalizedUrl).host.toLowerCase();
    } catch {
      return normalizedUrl.replace(/^https?:\/\//, '').split('/')[0];
    }
  }

  private resolveTemplateMeta(theme: any): any {
    const templateId =
      theme?.templateID
      ?? theme?.TemplateID
      ?? theme?.templateId
      ?? theme?.TemplateId
      ?? theme?.id
      ?? theme?.Id
      ?? null;
    const themeName = this.normalizeMatcherValue(theme?.name ?? theme?.Name ?? theme?.templateName ?? theme?.TemplateName);
    const themeUrls = [
      theme?.demoUrl,
      theme?.DemoUrl,
      theme?.previewUrl,
      theme?.PreviewUrl,
      theme?.webStoreHost,
      theme?.WebStoreHost
    ]
      .filter((v: any) => !!v)
      .map((v: any) => this.normalizeMatcherValue(v));

    return (
      this.TemplateData.find((t: any) => String(t.templateID) === String(templateId)) ||
      this.TemplateData.find((t: any) => {
        const templateName = this.normalizeMatcherValue(t.templateName);
        return themeName === templateName || themeName.includes(templateName) || templateName.includes(themeName);
      }) ||
      this.TemplateData.find((t: any) => {
        const templateHost = this.getHostFromUrl(t.previewUrl);
        const templateName = this.normalizeMatcherValue(t.templateName);
        return themeUrls.some((url: string) => url.includes(templateHost) || url.includes(templateName));
      })
    );
  }

  private getTemplateHomeImage(templateId: any, fallback?: string): string {
    const templateMeta = this.resolveTemplateMeta({ templateID: templateId });
    return templateMeta?.imageUrl || fallback || 'assets/images/theme_img.svg';
  }

  getThemePreviewImage(theme: any): string {
    return this.getTemplateHomeImage(theme?.templateID ?? theme?.id, theme?.imageUrl);
  }

  onThemePreviewError(event: Event): void {
    const imageElement = event.target as HTMLImageElement | null;
    if (!imageElement) {
      return;
    }

    const fallbackImage = 'assets/images/theme_img.svg';
    if (imageElement.src.includes(fallbackImage)) {
      return;
    }

    imageElement.src = fallbackImage;
  }

  getTemplatePreviewClass(theme: any): string {
    switch (String(theme?.templateID ?? theme?.id ?? '')) {
      case '1':
        return 'trendovo-bg';
      case '2':
        return 'luminest-bg';
      case '3':
        return 'timelore-bg';
      default:
        return '';
    }
  }

  dismissLayoutConfirmation(): void {
    this.pendingThemeSelection = null;
    this.showLayoutConfirmDialog = false;
  }

  confirmLayoutSelection(): void {
    if (this.pendingThemeSelection) {
      const wasCompleted = this.isStepCompleted(2);
      const existingTemplateId = this.selectedTheme?.templateID ?? this.selectedTheme?.id;
      const pendingTemplateId = this.pendingThemeSelection?.templateID ?? this.pendingThemeSelection?.id;
      const hasThemeChanged = String(existingTemplateId ?? '') !== String(pendingTemplateId ?? '');

      this.selectTheme(this.pendingThemeSelection);
      this.pendingThemeSelection = null;
      this.showLayoutConfirmDialog = false;

      if (wasCompleted && hasThemeChanged) {
        this.completeStep(2);
      }
      return;
    }
    this.showLayoutConfirmDialog = false;
    this.completeStep(2);
  }

  requestThemeSelection(theme: any): void {
    if (this.isStepCompleted(2)) {
      return;
    }
    this.pendingThemeSelection = theme;
    this.showLayoutConfirmDialog = true;
  }

  selectTheme(theme: any) {
    this.selectedTheme = theme;
    this.designStoreForm.patchValue({ selectedTheme: theme }, { emitEvent: false });

  }

  // Getter methods for Form Values - for backwards compatibility
  get productName(): string {
    return this.addProductsForm.get('productName')?.value || '';
  }

  set productName(value: string) {
    this.addProductsForm.patchValue({ productName: value }, { emitEvent: false });
  }

  get productCategory(): string {
    return this.addProductsForm.get('productCategory')?.value || '';
  }

  set productCategory(value: string) {
    this.addProductsForm.patchValue({ productCategory: value }, { emitEvent: false });
  }

  get productDescription(): string {
    return this.addProductsForm.get('productDescription')?.value || '';
  }

  set productDescription(value: string) {
    this.addProductsForm.patchValue({ productDescription: value }, { emitEvent: false });
  }

  get productPrice(): number | null {
    return this.addProductsForm.get('productPrice')?.value || null;
  }

  set productPrice(value: number | null) {
    this.addProductsForm.patchValue({ productPrice: value }, { emitEvent: false });
  }

  get businessName(): string {
    return this.businessDetailsForm.get('businessName')?.value || '';
  }

  get businessType(): any {
    return this.businessDetailsForm.get('businessType')?.value || null;
  }

  get businessDescription(): string {
    return this.businessDetailsForm.get('businessDescription')?.value || '';
  }

  get businessStreetAddress(): string {
    return this.businessDetailsForm.get('streetAddress')?.value || '';
  }

  get businessApartment(): string {
    return this.businessDetailsForm.get('apartment')?.value || '';
  }

  get businessCity(): string {
    return this.businessDetailsForm.get('city')?.value || '';
  }

  get businessState(): string {
    return this.businessDetailsForm.get('state')?.value || '';
  }

  get businessZipCode(): string {
    return this.businessDetailsForm.get('zipCode')?.value || '';
  }

  get businessPhone(): string {
    return this.businessDetailsForm.get('businessPhone')?.value || '';
  }

  get businessSubdomain(): string {
    return this.designStoreForm.get('subdomain')?.value || '';
  }

  set businessSubdomain(value: string) {
    this.designStoreForm.patchValue({ subdomain: value }, { emitEvent: false });
  }

  get shippingEnabled(): boolean {
    return this.shippingSettingsForm.get('shippingEnabled')?.value || false;
  }

  set shippingEnabled(value: boolean) {
    this.shippingSettingsForm.patchValue({ shippingEnabled: value }, { emitEvent: false });
  }

  get selectedBusinessType(): any {
    const businessTypeId = this.businessDetailsForm.get('businessType')?.value;
    // Return the full object by finding it in the businessTypes array
    if (businessTypeId) {
      return this.businessTypes.find(bt => bt.id === businessTypeId) || null;
    }
    return null;
  }

  set selectedBusinessType(value: any) {
    this.businessDetailsForm.patchValue({ businessType: value }, { emitEvent: false });
  }

  get businessCountry(): string {
    return this.businessDetailsForm.get('country')?.value || 'US';
  }

  set businessCountry(value: string) {
    this.businessDetailsForm.patchValue({ country: value }, { emitEvent: false });
  }

  showLayoutConfirmation(): void {
    this._confirmationService.confirm({
      message: 'Once you select a layout, you will not be able to change it later. Are you sure you want to continue?',
      header: 'Confirm Layout Selection',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.completeStep(2);
      },
      reject: () => {
        // User clicked Cancel or No
      }
    });
  }

  updateSubdomain(businessName?: string): void {
    // Auto-populate subdomain with cleaned business name
    const cleanedSubdomain = getSubdomainPreview(businessName || this.businessName);
    this.designStoreForm.patchValue({ subdomain: cleanedSubdomain }, { emitEvent: false });
  }

  validateSubdomain(subdomain?: string): void {
    // Reset availability status immediately
    this.subdomainAvailable = null;
    this.subdomainCheckError = '';

    // Enforce URL rules: only lowercase letters, numbers, and hyphens
    const currentSubdomain = subdomain || this.businessSubdomain;
    const validatedSubdomain = validateSubdomainFormat(currentSubdomain);

    this.designStoreForm.patchValue({ subdomain: validatedSubdomain }, { emitEvent: false });

    // Check if subdomain is valid and different from last checked
    if (validatedSubdomain && validatedSubdomain.length > 0 && validatedSubdomain !== this.lastCheckedSubdomain) {
      this.checkSubdomainAvailability(validatedSubdomain);
    } else if (!validatedSubdomain || validatedSubdomain.length === 0) {
      // Clear status if subdomain is empty
      this.subdomainAvailable = null;
    }
  }

  checkSubdomainAvailability(subdomain: string): void {
    // Skip if we already checked this exact subdomain
    if (this.lastCheckedSubdomain === subdomain) {
      return;
    }

    this.lastCheckedSubdomain = subdomain;
    this.subdomainCheckLoading = true;
    this.subdomainCheckError = '';

    const payload = {
      action: 'check_subdomain',
      subdomain: subdomain
    };

    let data =
    {
      // "first_name":this._signup.personalInfo.firstName,
      // "last_name": this._signup.personalInfo.lastName,
      // "email": this._signup.personalInfo.email,
      // "password": this._signup.personalInfo.password,
      "business_name": subdomain,
      //   "business_id": this._signup.businessInfo.businessId.toString(),
      //   "verification_status": 1,     //hard
      //  "saasy_user_id": this._signup.businessInfo.userID.toString(),
      //  "phone_number": this._signup.personalInfo.phone.toString(),
      //  "terms_and_conditions":"1",  //hard
      //  "platform":"woocommerce",  //hard
      //  "register":"no"  //hard
    }



    this._signup.CheckSubdomainExists(data).subscribe({
      next: (response) => {
        if (response.data.message === "AVAILABLE") {
          this.subdomainAvailable = true;
          this.subdomainCheckError = '';
        }
        else {
          this.subdomainAvailable = false;
          this.subdomainCheckError = 'This store name is not available. Please choose a different one.';
        }
      },
      error: (error) => {
        console.log(error)
      }
    });
    this.subdomainCheckLoading = false;
    return;
    // Use HTTP to avoid mixed-content policy issues during development
    const apiUrl = 'http://setuphub.lincsell.com/handleFolderRequest.php';


    this._http.post(apiUrl, payload, {
      headers: {
        'Content-Type': 'application/json',
        'do-need-to-process-interceptor': 'skip'
      },
      responseType: 'json',
      withCredentials: false
    })
      .subscribe({
        next: (response: any) => {
          this.subdomainCheckLoading = false;

          if (response.status === true) {
            this.subdomainAvailable = true;
            this.subdomainCheckError = '';
          } else {
            this.subdomainAvailable = false;
            this.subdomainCheckError = response.message || 'This store name is not available. Please choose a different one.';


          }
        },
        error: (error: any) => {
          this.subdomainCheckLoading = false;
          this.subdomainAvailable = false;

          if (error.status === 0) {
            this.subdomainCheckError = 'Network error: Cannot reach the API server.';
          } else if (error.status === 404) {
            this.subdomainCheckError = 'API endpoint not found.';
          } else if (error.status === 500) {
            this.subdomainCheckError = 'Server error. Please try again later.';
          } else if (error.status === 403) {
            this.subdomainCheckError = 'Access forbidden.';
          } else {
            this.subdomainCheckError = error.error?.message || 'Failed to verify subdomain availability.';
          }


        }
      });

  }

  // Test API connectivity
  testAPIConnection(): void {
    this._http.post('http://setuphub.lincsell.com/handleFolderRequest.php?test=1', {}, {
      headers: {
        'do-need-to-process-interceptor': 'skip'
      }
    })
      .subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'API Connection OK',
            detail: 'API server is reachable and responding correctly',
            sticky: false
          });
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'API Connection Failed',
            detail: `Cannot reach API server. Status: ${error.status}`,
            sticky: false
          });
        }
      });
  }

  onPhoneInput(event: any): void {
    const input = event.target.value;
    const formatted = formatPhoneNumber(input);
    this.businessDetailsForm.patchValue({ businessPhone: formatted }, { emitEvent: false });
    event.target.value = formatted;
  }

  addAnotherProduct() {
    this.clearProductImageSelection();
    this.isAddingAnotherProduct = true; // Show form instead of summary
    this.activeIndex = 2; // Keep Step 3 open for adding more products
    this.persistActiveStep(this.activeIndex);
  }

  toggleShipping() {
    this.shippingEnabled = !this.shippingEnabled;
  }

  onShippingFormChanged(formValue: any): void {
    // Optional: Handle form changes if needed
    console.log('Shipping form changed:', formValue);
  }


  validateProductForm(): boolean {
    // Check if all required fields are filled
    if (!this.productName || this.productName.trim() === '') {
      this._messageService.add({ severity: 'warn', summary: 'Required Field', detail: 'Product name is required.', sticky: false });
      return false;
    }
    if (!this.productCategory || this.productCategory.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'Product category is required.', sticky: false });
      return false;
    }
    if (!this.productDescription || this.productDescription.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'Product description is required.', sticky: false });
      return false;
    }
    if (this.productPrice === null || this.productPrice === undefined || this.productPrice <= 0) {
      this._messageService.add({ severity: 'error', summary: 'Invalid Price', detail: 'Product price must be greater than 0.', sticky: false });
      return false;
    }
    return true;
  }

  validateStoreSetupForm(): boolean {
    // Step 1 validation - Business Details (Name, Type, Description, Address, City, State, ZIP, Phone)
    if (!this.businessName || this.businessName.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'Business name is required.', sticky: false });
      return false;
    }
    if (!this.selectedBusinessType) {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'Please select a business type.', sticky: false });
      return false;
    }
    if (!this.businessDescription || this.businessDescription.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'Business description is required.', sticky: false });
      return false;
    }
    if (!this.businessStreetAddress || this.businessStreetAddress.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'Street address is required.', sticky: false });
      return false;
    }
    if (!this.businessCity || this.businessCity.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'City is required.', sticky: false });
      return false;
    }
    if (!this.businessState || this.businessState.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'State is required.', sticky: false });
      return false;
    }
    if (!this.businessZipCode || this.businessZipCode.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'ZIP code is required.', sticky: false });
      return false;
    }
    if (!this.businessPhone || this.businessPhone.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'Business phone number is required.', sticky: false });
      return false;
    }
    // Validate phone number format
    if (!validatePhoneNumber(this.businessPhone)) {
      this._messageService.add({ severity: 'error', summary: 'Invalid Phone Number', detail: 'Business phone number must contain at least 10 digits.', sticky: false });
      return false;
    }
    return true;
  }

  validateDesignForm(): boolean {
    // Step 2 validation - Store URL and Theme
    if (!this.businessSubdomain || this.businessSubdomain.trim() === '') {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'Store URL is required.', sticky: false });
      return false;
    }
    if (!this.selectedTheme) {
      this._messageService.add({ severity: 'error', summary: 'Required Field', detail: 'Please select a layout theme.', sticky: false });
      return false;
    }
    return true;
  }

  validateAddressForm(): boolean {
    // Step 3 validation - Products
    // This step validates products which are validated via validateProductForm
    return true;
  }

  validateAndCompleteStep(step: number): void {
    let isValid = true;

    switch (step) {
      case 1:
        isValid = this.validateStoreSetupForm();
        break;
      case 2:
        isValid = this.validateDesignForm();
        break;
      case 3:
        isValid = this.validateProductForm();
        break;
    }

    if (isValid) {
      this.completeStep(step);
    }
  }

  // Check if only Step 3 (Add Products) is remaining
  isOnlyAddProductsRemaining(): boolean {
    // Step 3 is remaining if:
    // - Step 3 is not completed
    // - Step 1 and Step 2 are both completed
    return !this.isStepCompleted(3) && this.isStepCompleted(1) && this.isStepCompleted(2);
  }

  skipStep(currentStep: number) {
    // Special handling for Step 3 (Add Products)
    if (currentStep === 3) {
      // If skipping Step 3 when it's the only remaining step, mark it complete and go to dashboard
      this.completedSteps.add(3);
      setTimeout(() => {
        this.isSuccess = true;
        this.persistActiveStep(null);
        this.startSuccessCountdown(); // START TIMER AUTO
      }, 300);
      return;
    }

    // For PrimeNG accordion, 0 = step 1, 1 = step 2...
    // currentStep is passed as 1, 2, 3..
    // So index of current step is currentStep - 1.
    const nextIndex = currentStep; // which is essentially (currentStep - 1) + 1
    if (nextIndex < this.totalSteps) {
      this.activeIndex = nextIndex;
      this.persistActiveStep(this.activeIndex);
    } else {
      this.activeIndex = null;
      this.persistActiveStep(null);
    }
  }

  completeStep(step: number) {
    // Validate step 3 form (Add Products)
    if (step === 3) {
      if (!this.validateProductForm()) {
        return; // Stop if validation fails
      }

      // Create product using the same logic as create-product component
      this.createAndSaveProduct();
      return; // Don't proceed until save completes
    }

    // Save Step 1 Business Details to API
    if (step === 1) {
      this.saveStepOneBusinessDetails();
      return; // Don't proceed until save completes
    }

    // Save Step 2 Layout/Theme Selection to API
    if (step === 2) {
      this.selectedTheme.webStoreHost = this.designStoreForm.get('subdomain')?.value;
      this.saveLayoutFn(this.selectedTheme);
      return; // Don't proceed until save completes
    }

    this.completedSteps.add(step);
    this.checkAndShowSuccessIfAllStepsComplete();
  }

  private checkAndShowSuccessIfAllStepsComplete(): void {
    // Check if all required steps are completed
    const allStepsComplete = this.totalSteps > 0 && this.completedSteps.size >= this.totalSteps;

    if (allStepsComplete) {
      // All steps are complete, show success view
      setTimeout(() => {
        this.isSuccess = true;
        this.activeIndex = null; // Close accordion
        this.persistActiveStep(null);
        this.startSuccessCountdown(); // START TIMER AUTO
      }, 300);
    } else {
      // Find and open the next incomplete step
      for (let step = 1; step <= this.totalSteps; step++) {
        if (!this.isStepCompleted(step)) {
          // Found an incomplete step, open it
          setTimeout(() => {
            this.activeIndex = step - 1;
            this.persistActiveStep(this.activeIndex);
          }, 200);
          return;
        }
      }
    }
  }

  private createAndSaveProduct(): void {
    try {
      this.isLoadingStepStatus = true;

      // Check if category exists, create if necessary
      const categoryName = this.productCategory?.trim();

      if (!categoryName) {
        this._connMessageService.showToastMessage('Please select or enter a category', 'warn');
        this.isLoadingStepStatus = false;
        return;
      }

      // Check if category exists in _abd.categories
      const existingCategory = this._abd.categories?.find(
        c => c.categoryName?.toLowerCase() === categoryName.toLowerCase()
      );

      let categoryId = existingCategory?.id;

      if (!existingCategory) {
        // Category doesn't exist, need to create it first
        this.createCategoryAndSaveProduct(categoryName);
        return;
      }

      // Category exists, proceed with product creation
      this.saveProductWithCategoryId(categoryId);
    } catch (error) {
      this.isLoadingStepStatus = false;
      this._connMessageService.showToastMessage('Error processing product', 'error');
    }
  }

  private createCategoryAndSaveProduct(categoryName: string): void {
    try {
      const currentSetup = this.getCurrentBusinessSetup();
      const departmentId = currentSetup?.departmentId || 1;

      // Create category model following the same structure as new-edit-category component
      const categoryModel = {
        id: null,
        categoryName: categoryName,
        departmentId: departmentId,
        isActive: true,
        addedBy: this._global.userInfo?.userID ?? this._global.userInfo?.userId ?? currentSetup?.userID ?? currentSetup?.userId,
        isDefault: false,
        partitionKey: null,
        createdDate: new Date(),
        lastEdited: new Date()
      };

      // Save the category, then wait for getAllCategories to complete, then find and use the category
      this._categoryService.createUpdateCategory(categoryModel)
        .pipe(
          switchMap((res: any) => {
            if (res && res.success) {
              // Category created successfully, now refresh the categories list
              this._abd.getAllCategories();

              // Return a stream that waits for the category to be found
              return this.pollForCategory(categoryName);
            } else {
              throw new Error('Failed to create category');
            }
          }),
          timeout(5000), // Total timeout: 5 seconds
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: (categoryId: string) => {
            // Category found with ID, proceed with product creation
            this.saveProductWithCategoryId(categoryId);
          },
          error: (error: any) => {
            this.isLoadingStepStatus = false;
            const errorMessage = error?.message || error?.error?.message || 'Error creating or retrieving category';
            this._connMessageService.showToastMessage(errorMessage, 'error');
          }
        });
    } catch (error) {
      this.isLoadingStepStatus = false;
      this._connMessageService.showToastMessage('Error creating category', 'error');
    }
  }

  private pollForCategory(categoryName: string): any {
    // Poll for the category with exponential backoff using RxJS
    const pollInterval$ = new Promise<string>((resolve, reject) => {
      const maxAttempts = 8;
      const baseDelay = 300;
      let attempt = 0;

      const checkCategory = () => {
        const newCategory = this._abd.categories?.find(
          c => c.categoryName?.toLowerCase() === categoryName.toLowerCase()
        );

        if (newCategory && newCategory.id) {
          resolve(newCategory.id);
          return;
        }

        if (attempt >= maxAttempts) {
          reject(new Error('Category created but could not retrieve ID after multiple attempts'));
          return;
        }

        attempt++;
        const delayMs = baseDelay * Math.pow(1.5, attempt - 1);
        setTimeout(checkCategory, delayMs);
      };

      checkCategory();
    });

    return new Promise<string>((resolve, reject) => {
      pollInterval$.then(resolve).catch(reject);
    });
  }

  private async saveProductWithCategoryId(categoryId: any): Promise<void> {
    try {
      const currentSetup = this.getCurrentBusinessSetup();
      const isFirstProductForBusiness = Number(this.productCount || 0) === 0;
      // Generate SKU and Barcode (same logic as create-product component)
      const sku = this._global.generateSKU?.() || 'SKU-' + Date.now();
      let barcode = this._global.generateSKU?.() || 'BAR-' + Date.now();
      barcode = (parseInt(barcode) + 23).toString();

      // Get default values for mandatory fields not shown in UI
      const departmentId = currentSetup?.departmentId || 1;
      const manufacturerId = currentSetup?.manufacturerId || 1;
      const vendorId = currentSetup?.vendorId || 1;
      const price = this.productPrice || 0;
      const shippingDimention = {
        weightUOM: 0,
        width: 0,
        dimensionUOM: 3,
        height: 0,
        weight: 0,
        length: 0,
      };

      const skus = [{
        skuCode: sku,
        barcode: barcode,
        manufacturerBarcode: barcode,
        title: this.productName,
        salePrice: price,
        salePriceA: price,
        salePriceB: 0,
        salePriceC: 0,
        costPrice: 0,
        discountPrice: 0,
        taxRateId: 1,
        taxable: true,
        minLevel: 0,
        maxLevel: 0,
        reorderLevel: 0,
        minOrderQty: 1,
        maxOrderQty: 999,
        defaultPrice: 1,
        sellQuantity: 1,
        uomId: 1,
        caseQty: 1,
        openingStock: 0,
        isVisible: true,
        isDefault: true,
        autoPurchaseOrder: false,
        shippingDimention: shippingDimention,
        barcodes: [],
        skus: [],
        tags: []
      }];

      const finalImage = await this.resolveProductImageData();

      // Build product images array
      const itemPictures = [];
      if (finalImage) {
        itemPictures.push({
          orderNo: 1,
          picture: finalImage,
          pictureThumbnail: finalImage,
          isFeature: true,
          isDefault: true
        });
      }

      const productModel = {
        "itemName": this.productName,
        "notes": '',
        "descriptionURL": '',
        "itemType": 1,
        "soldAs": 1,
        "ageRestriction": 0,
        "gender": 0,
        "allowDiscount": 1,
        "isTaxable": true,
        "isOpnDept": false,
        "showNotesWhen": false,
        "printOnInvoice": false,
        "printAsQRCodeOnReceipt": false,
        "trackStock": false,
        "isActive": true,
        "createdAt": new Date(),
        "lastEdited": new Date(),
        "addedBy": this._global.userInfo?.userID ?? this._global.userInfo?.userId ?? currentSetup?.userID ?? currentSetup?.userId,
        "departmentId": departmentId,
        "categoryId": categoryId,
        "manufacturerId": manufacturerId,
        "vendorId": vendorId,
        "allowNegativeStock": false,
        "description": this.productDescription,
        "image": finalImage,
        "imageThumbnail": finalImage,
        "grouping": 0,
        "isVariantProduct": false,
        "crsItemSKUs": skus,
        "itemVariantOptions": [],
        "itemVariantOptionValues": [],
        "itemVariantCombinations": [],
        "openingStocks": [],
        "itemPictures": itemPictures,
        "sellOnline": true,
        "productSellType": "online",
        "isReturnable": true,
      };

      const completeProductSaveFlow = (): void => {
        this._connMessageService.showToastMessage(
          'Product added successfully!',
          'success'
        );

        this.productCount++;
        this.productName = '';
        this.productCategory = '';
        this.productDescription = '';
        this.productPrice = null;
        this.clearProductImageSelection();
        this.isAddingAnotherProduct = false;
        this.completedSteps.add(3);
        this.checkAndShowSuccessIfAllStepsComplete();
      };

      this._productServices.saveProductV3(productModel).subscribe(
        (res: any) => {
          if (res.success) {
            this._productServices.notifyFirstProductCreated(isFirstProductForBusiness);
            completeProductSaveFlow();
            return;
          }

          this._connMessageService.showToastMessage(
            res.message || 'Failed to save product',
            'error'
          );
          this.isLoadingStepStatus = false;
        },
        (error: any) => {
          this._connMessageService.showToastMessage(
            error?.error?.message || 'Error saving product!',
            'error'
          );
          this.isLoadingStepStatus = false;
        },
        () => {
          if (this.isLoadingStepStatus) {
            this.isLoadingStepStatus = false;
          }
        }
      );
    } catch (error) {
      this._connMessageService.showToastMessage(
        'Error saving product!',
        'error'
      );
      this.isLoadingStepStatus = false;
    }
  }

  private saveStepOneBusinessDetails(): void {
    try {
      this.isLoadingStepStatus = true;
      const currentSetup = this.getCurrentBusinessSetup();

      // Get the selected business type object
      const selectedType = this.selectedBusinessType;

      // Combine industry and description into single field: "Industry Name || description"
      const industryWithDescription = selectedType?.name && this.businessDescription
        ? `${selectedType.name} || ${this.businessDescription}`
        : selectedType?.name || '';

      // Combine street address and apartment with || separator
      const fullAddress = this.businessApartment
        ? `${this.businessStreetAddress} || ${this.businessApartment}`
        : this.businessStreetAddress;

      // Build the request object with all business details from Step 1
      const bizInfoVariable = {
        businessDisplayName: this.businessName,
        industry: industryWithDescription,
        description: this.businessDescription,
        address: fullAddress,
        city: this.businessCity,
        state: this.businessState,
        zip: this.sanitizeZipCode(this.businessZipCode),
        country: this.businessCountry || 'US',
        phone: this.businessPhone,
        businessId: currentSetup?.businessId
      };

      // Call API to save business information
      this._businessSetup.saveBusinessInformationV2(bizInfoVariable).subscribe(
        (res: any) => {
          if (res.success) {
            // this._connMessageService.showToastMessage(
            //   'Business Information saved successfully!',
            //   'success'
            // );

            // Update global user info with new data
            if (this._global.userInfo?.businessSetup?.[0]) {
              // Map the sent data back to API response format
              const updateData = {
                businessName: this.businessName,
                businessDisplayName: this.businessName,
                industry: industryWithDescription,
                description: this.businessDescription,
                address: fullAddress,
                city: this.businessCity,
                state: this.businessState,
                zip: this.sanitizeZipCode(this.businessZipCode),
                country: this.businessCountry || 'US',
                phone: this.businessPhone
              };
              Object.assign(this._global.userInfo.businessSetup[0], updateData);
            } else if (currentSetup) {
              Object.assign(currentSetup, {
                businessName: this.businessName,
                businessDisplayName: this.businessName,
                industry: industryWithDescription,
                description: this.businessDescription,
                address: fullAddress,
                city: this.businessCity,
                state: this.businessState,
                zip: this.sanitizeZipCode(this.businessZipCode),
                country: this.businessCountry || 'US',
                phone: this.businessPhone
              });
              sessionStorage.setItem('business-Info', JSON.stringify(currentSetup));
            }

            // Mark step as completed
            this.completedSteps.add(1);
            this._businessSetup.notifyOnlineStoreInitiated();
            this._userService.craeteDefaultLocation({
              businessDisplayName: this.businessName,
              businessName: this.businessName,
              address: fullAddress,
              city: this.businessCity,
              state: this.businessState,
              zip: this.sanitizeZipCode(this.businessZipCode),
              country: this.businessCountry || 'US',
              phone: this.businessPhone
            });
            // DO NOT set step1CompletedOnPrefetch = true here
            // This way, if user manually completes Step 1, they can edit it later
            // Only pre-completed steps are locked

            // Check if all steps are complete or navigate to next incomplete step
            this.checkAndShowSuccessIfAllStepsComplete();
          } else {
            // API returned failure response
            this._connMessageService.showToastMessage(
              res.message || 'Failed to save business information',
              'error'
            );
            this.isLoadingStepStatus = false;
          }
        },
        (error: any) => {
          // API call failed with error
          console.error('Error saving Step 1 data:', error);
          this._connMessageService.showToastMessage(
            error?.error?.message || 'Error saving business information!',
            'error'
          );
          this.isLoadingStepStatus = false;
        },
        () => {
          // Complete callback - called after success or error
          // Only hide spinner if it's still showing (in case of success)
          if (this.isLoadingStepStatus) {
            this.isLoadingStepStatus = false;
          }
        }
      );
    } catch (error) {
      console.error('Exception while saving Step 1:', error);
      this._connMessageService.showToastMessage(
        'Error saving business information!',
        'error'
      );
      this.isLoadingStepStatus = false;
    }
  }
  constructor(
    public _abd: AppBaseDataService,
    public _businessSetup: BusinessSetupService,
    public _userService: UserService,
    private _router: Router,
    private _connMessageService: ConnMessageService,
    public _locationService: LocationsService,
    private _location: Location,
    private _confirmationService: ConfirmationService,
    private _messageService: MessageService,
    public _businessSetupService: BusinessSetupService,
    private _formBuilder: FormBuilder,
    private _http: HttpClient,
    private _productServices: ProductsService,
    private _formDataService: FormDataService,
    public _signup: SignupService,
    private _cdr: ChangeDetectorRef,
    private _categoryService: CategoryService,
    private _domSanitizer: DomSanitizer,
  ) {
    this.initializeForms();
    this._abd.hideSpinner();
    this.loadFormData();
  }

  private loadFormData(): void {
    // Load business types
    this._formDataService.getBusinessTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe(types => {
        this.businessTypes = types;
      });

    // Load US states
    this._formDataService.getUsStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe(states => {
        this.usStates = states;
      });

    // Load rate types
    this._formDataService.getRateTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe(types => {
        this.rateTypes = types;
      });
  }

  private initializeForms(): void {
    // Step 1: Business Details Form
    this.businessDetailsForm = this._formBuilder.group({
      businessName: ['', [Validators.required]],
      businessType: ['', [Validators.required]],
      businessDescription: ['', [Validators.required]],
      streetAddress: ['', [Validators.required]],
      apartment: [''],
      country: ['US', [Validators.required]],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      zipCode: ['', [Validators.required]],
      businessPhone: ['', [Validators.required]]
    });

    // Step 2: Design Store Form
    this.designStoreForm = this._formBuilder.group({
      subdomain: ['', [Validators.required]],
      businessLogo: [''],
      selectedTheme: ['', [Validators.required]]
    });

    // Step 3: Add Products Form
    this.addProductsForm = this._formBuilder.group({
      productName: ['', [Validators.required]],
      productCategory: ['', [Validators.required]],
      productDescription: ['', [Validators.required]],
      productPrice: [null, [Validators.required, Validators.min(0.01)]],
      productImage: ['']
    });

    // // Step 5: Shipping Settings Form - with FormArray for multi-zone support
    // this.shippingSettingsForm = this._formBuilder.group({
    //   shippingEnabled: [false],
    //   originAddress: [''],
    //   originCity: [''],
    //   originState: [''],
    //   originZip: [''],
    //   originCountry: ['US'],
    //   zones: this._formBuilder.array([])
    // });
  }

  resetDemo() {
    this.completedSteps.clear();
    this.step1CompletedOnPrefetch = false;
    this.activeIndex = 0;
    this.persistActiveStep(this.activeIndex);
    this.isSuccess = false;
    this.clearProductImageSelection();
    this.logoPreview = null;
    this.isAddingAnotherProduct = false;
    this.selectedTheme = null;
    this.productCount = 0;
    this.products = [];
    this.successTimerStarted = false;
    this.redirectCountdown = 0;
    this.isCountingDown = false;
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }

    // Reset prefilled fields tracking
    Object.keys(this.prefilledFields).forEach(key => {
      this.prefilledFields[key as keyof typeof this.prefilledFields] = false;
    });

    // Reset all forms
    this.businessDetailsForm.reset({
      country: 'US'
    });
    this.designStoreForm.reset();
    this.addProductsForm.reset();
    this.shippingSettingsForm.reset({
      shippingEnabled: false,
      originAddress: '',
      originCity: '',
      originState: '',
      originZip: '',
      originCountry: 'US',
      zones: []
    });
  }
  saveLayoutFn(currentTemplate?: any) {
    const businessName = this.normalizeString(this.selectedTheme?.webStoreHost);
    const businessId = this.resolveUpdatedomainBusinessId();

    if (!businessName) {
      this._connMessageService.showToastMessage(
        'Store URL is required before saving layout.',
        'error'
      );
      this.isLoadingStepStatus = false;
      return;
    }

    if (!businessId) {
      this._connMessageService.showToastMessage(
        'Unable to resolve business id. Please refresh and try again.',
        'error'
      );
      this.isLoadingStepStatus = false;
      return;
    }

    let data = {
      email: this._global.userInfo.email ?? this._global.userInfo.username,
      business_name: businessName,
      business_id: businessId,
    }

    // Show spinner before making API call
    this.isLoadingStepStatus = true;

    // Call updatedomain first, and only proceed if successful
    this._signup.updatedomain(data).subscribe({
      next: (response) => {
        console.log('updatedomain response:', response);

        // Check if updatedomain was successful
        if (response.data.message !== "UPDATED") {
          this._connMessageService.showToastMessage(
            'Failed to update domain. Please try again.',
            'error'
          );
          this.isLoadingStepStatus = false;
          return;
        }

        // If updatedomain was successful, proceed to save theme
        this.saveThemeAfterDomainUpdate(currentTemplate);
      },
      error: (error) => {
        console.error('Error updating domain:', error);
        this._connMessageService.showToastMessage(
          error?.error?.message || 'Error updating domain. Please try again.',
          'error'
        );
        this.isLoadingStepStatus = false;
      }
    });
  }

  private resolveUpdatedomainBusinessId(): string {
    const setup = this.getCurrentBusinessSetup() as any;
    const signupBusinessInfo = (this._signup as any)?.businessInfo as any;

    const candidates = [
      setup?.businessNameWithDomain,
      setup?.businessId,
      setup?.id,
      signupBusinessInfo?.businessId,
      this.selectedTheme?.webStoreHost,
      this.businessSubdomain,
      this.businessName
    ];

    for (const candidate of candidates) {
      const normalized = this.normalizeString(candidate);
      if (
        normalized &&
        normalized !== '0' &&
        normalized.toLowerCase() !== 'null' &&
        normalized.toLowerCase() !== 'undefined'
      ) {
        return normalized;
      }
    }

    return '';
  }

  private normalizeString(value: any): string {
    return String(value ?? '').trim();
  }

  private sanitizeZipCode(value: any): string {
    return String(value ?? '').replace(/\D/g, '').slice(0, 5);
  }

  private saveThemeAfterDomainUpdate(currentTemplate: any): void {
    let obj = {
      id: currentTemplate?.id,
      templateID: currentTemplate?.templateID,
      webStoreHost: currentTemplate?.webStoreHost + '.lincsell.com',
      demoUrl: currentTemplate?.demoUrl,
      isDefault: true,
      isAvailable: currentTemplate?.isAvailable,
      imageUrl: currentTemplate?.imageUrl,
      isSelectedLater: currentTemplate?.isSelectedLater,
      createdDate: currentTemplate?.createdDate,
    }

    this.isLoadingStepStatus = true;

    this._businessSetup.saveThemeInformation(obj).subscribe(
      (res) => {
        if (res.data === true) {
          this._businessSetup.getThemeConfigSettings().subscribe((res) => {
            if (res.success) {

            }
          })
          this._connMessageService.showToastMessage(
            `Theme Configuration saved successfully!`,
            'success'
          )

          // Mark step 2 as completed
          this.completedSteps.add(2);

          // Check if all steps are complete or navigate to next incomplete step
          this.checkAndShowSuccessIfAllStepsComplete();
        } else {
          // Theme save failed
          this._connMessageService.showToastMessage(
            `This template is not available. Please choose another one.`,
            'error'
          )
          this.isLoadingStepStatus = false;
        }
      },
      (error: any) => {
        // API call failed with error
        console.error('Error saving theme:', error);
        this._connMessageService.showToastMessage(
          error?.error?.message || `Error saving theme configuration!`,
          'error'
        );
        this.isLoadingStepStatus = false;
      },
      () => {
        // Complete callback - ensure spinner is hidden if not already
        if (this.isLoadingStepStatus) {
          this.isLoadingStepStatus = false;
        }
      }
    )
  }
  // Prevent Step 1 from collapsing if it was pre-completed
  onAccordionOpen(event: any): void {
    // When Step 2 is opened, validate subdomain format and check availability
    if (event.index === 1) {
      if (this.businessSubdomain && this.businessSubdomain.trim() !== '') {
        // Validate subdomain format first
        const validatedSubdomain = validateSubdomainFormat(this.businessSubdomain);
        this.designStoreForm.patchValue({ subdomain: validatedSubdomain }, { emitEvent: false });

        // Check if subdomain has changed or hasn't been checked yet
        if (validatedSubdomain !== this.lastCheckedSubdomain) {
          this.checkSubdomainAvailability(validatedSubdomain);
        }
      }
    }

    this.persistActiveStep(event?.index ?? null);
  }

  onAccordionClose(event: any): void {
    if (this.activeIndex === null || this.activeIndex === undefined) {
      this.persistActiveStep(null);
    }
  }

  editStoreUrl(): void {
    // Reset success state and open Step 2 for editing the store URL
    this.isSuccess = false;
    this.activeIndex = 1; // Step 2 (0-based index)
    this.persistActiveStep(this.activeIndex);
  }

  private startSuccessCountdown(): void {
    // Only start the timer once per success view
    if (this.successTimerStarted) {
      return;
    }
    this.successTimerStarted = true;
    this.redirectCountdown = 10;
    this.isCountingDown = true;

    // Use RxJS interval for auto-update
    this.countdownSubscription = interval(1000)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.redirectCountdown--;

        if (this.redirectCountdown <= 0) {
          this.isCountingDown = false;
          if (this.countdownSubscription) {
            this.countdownSubscription.unsubscribe();
          }
          this.navigateToDashboardAfterSetup();
        }
      });
  }

  goToDashboard(): void {
    this.navigateToDashboardAfterSetup();
  }

  private navigateToDashboardAfterSetup(): void {
    this.persistActiveStep(null);
    sessionStorage.removeItem('allow-store-setup-v2');
    sessionStorage.removeItem('allow-store-setup-offer');
    sessionStorage.setItem('force-dashboard-after-store-setup-v2', '1');
    localStorage.setItem('showSetup', 'false');
    localStorage.setItem('isFirstLogin', 'true');
    if (this._global.userInfo?.businessSetup?.[0]) {
      this._global.userInfo.businessSetup[0].isTour = true;
    }

    this._userService.isTourComplete().subscribe({
      next: () => { },
      error: () => { }
    });

    this._router.navigateByUrl(this._abd.getDashboardRoute());
  }
}


